import React, { useMemo } from 'react';
import { ActivityTableHeader } from './ActivityTableHeader';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { isPreviewContactId, PREVIEW_SMS } from '../../../../pages/previewMockData';
import { EmptyStateWithIcon } from '../../../common/EmptyStateWithIcon';
import { MessageCircle } from 'lucide-react';
import { useContactSmsGQL } from '../../../../hooks/graphql/contacts/useContactSmsGQL';
import { SmsChatList, type SmsChatMessage } from './SmsChatList';

interface SmsTabProps {
  contactId: string;
  contactFirstName?: string;
  /** Jump scroll to the newest message on first load.
   *  Modal usage wants this (the modal is short and a jump-to-newest
   *  feels natural). The contact edit tab is tall and embedded, so the
   *  jump reads as the page itself moving — leave it off there and let
   *  the "See latest messages" pill carry the action. */
  autoScrollOnLoad?: boolean;
}

/**
 * Contact-side SMS thread. Owns the data layer (useContactSmsGQL,
 * pagination via loadOlder) and delegates rendering to SmsChatList —
 * the same presentational component the dashboard SmsThreadModal uses,
 * so the two surfaces stay visually identical.
 */
export const SmsTab: React.FC<SmsTabProps> = ({
  contactId,
  contactFirstName,
  autoScrollOnLoad = false,
}) => {
  const { organization } = useOrganizationStore();
  const tz = organization?.timezone;
  const assistantName = organization?.assistant_name || 'Remi';

  const isPreview = isPreviewContactId(contactId);
  // Server-side paginated, newest-first; reversed below so latest sits
  // at the bottom (inbox style). Older pages load via the "Load older"
  // button surfaced by SmsChatList.
  const live = useContactSmsGQL(isPreview ? '' : contactId);

  const messages: SmsChatMessage[] = useMemo(() => {
    if (isPreview) {
      return (PREVIEW_SMS as Array<{
        smsId: string;
        direction: 'inbound' | 'outbound';
        fromNumber: string;
        fromName?: string;
        body: string;
        sentAt: string;
        deliveredAt?: string | null;
        readAt?: string | null;
      }>).map((m) => ({
        smsId: m.smsId,
        body: m.body,
        direction: m.direction,
        fromName: m.fromName,
        fromNumber: m.fromNumber,
        sentAt: m.sentAt,
        deliveredAt: m.deliveredAt,
        readAt: m.readAt,
      }));
    }
    // Hook returns newest-first; reverse to oldest-first for the
    // chat layout. SmsChatList expects ASC.
    return [...live.items].reverse().map((m) => ({
      smsId: m.messageId,
      body: m.body || '',
      direction: (m.direction === 'inbound' ? 'inbound' : 'outbound') as
        | 'inbound'
        | 'outbound',
      fromNumber: m.fromAddress || '',
      sentAt: m.sentAt || m.createdAt,
      deliveredAt: m.deliveredAt,
      readAt: m.openedAt,
    }));
  }, [isPreview, live.items]);

  const loading = !isPreview && live.loading && messages.length === 0;

  if (loading) {
    return (
      <div className="flex flex-col h-full min-h-0 gap-4">
        <ActivityTableHeader title="SMS" count={0} isLoading />
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
          Loading messages…
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-col h-full min-h-0 gap-4">
        <ActivityTableHeader title="SMS" count={0} isLoading={false} />
        <EmptyStateWithIcon
          message="No SMS messages with this contact yet."
          icon={<MessageCircle className="h-6 w-6 lg:h-8 lg:w-8 text-muted-foreground" />}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 gap-3 relative">
      <ActivityTableHeader title="SMS" count={live.total || messages.length} isLoading={false} />
      <SmsChatList
        messages={messages}
        timezone={tz}
        contactFirstName={contactFirstName}
        assistantName={assistantName}
        autoScrollToLatestOnLoad={autoScrollOnLoad}
        hasOlder={!isPreview && live.hasMore}
        onLoadOlder={live.loadOlder}
        isLoadingOlder={live.loading && messages.length > 0}
        resetKey={contactId}
      />
    </div>
  );
};
